import { useEffect } from 'react'
import { isTrackingUiEnabled } from '../config/analyticsFeatureFlags'
import { useCookieConsent } from '../context/CookieConsentContext'

const BASE_URL = process.env.REACT_APP_API_URL || ''

// ---------------------------------------------------------------------------
// useVisitTracking
//
// Sends a same-origin anonymous visit request to POST /api/v1/analytics/visits
// when all gates pass:
//   1. isTrackingUiEnabled engineering flag is on
//   2. eventId is present
//   3. caller is not the event owner (backend also enforces exclusion)
//   4. user has given active analytics consent
//
// Fire-and-forget: the 202 response is intentionally discarded. Errors are
// swallowed silently — visit recording must never degrade the event page.
// No identity, receipt, or response content is exposed to the caller.
// ---------------------------------------------------------------------------
const useVisitTracking = ({ eventId, isOwner, source = 'direct' }) => {
  const { consent } = useCookieConsent()

  useEffect(() => {
    if (!isTrackingUiEnabled) return
    if (!eventId) return
    if (isOwner) return
    if (consent?.analyticsInternal !== true) return

    const controller = new AbortController()

    fetch(`${BASE_URL}/api/v1/analytics/visits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ eventId, source }),
      signal: controller.signal
    }).catch(() => {
      // Intentionally silent — visit errors must not affect the event page
    })

    return () => {
      controller.abort()
    }
  }, [eventId, isOwner, consent, source])
}

export default useVisitTracking
